import { memo } from "react";
import { styled } from "@mui/material/styles";
import ProgressBar from "./components/ProgressBar";
import TargetCircle from "./components/TargetCircle";

export interface TargetGraphProps {
  /**
   * Array of target values representing milestones
   */
  targets: {
    value: number;
    rate: number;
    gapToTarget?: number;
  }[];
  /**
   * Invoice value (shown as light green progress)
   */
  invoiceValue: number;
  /**
   * Orderbook value (shown as dark green progress)
   */
  orderbookValue: number;

  gapToNextTarget?: number;

  /**
   * Optional class name for styling
   */
  className?: string;

  currency: string;
}

const Root = styled("div")({
  position: "relative",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  alignItems: "flex-start",
  justifyContent: "center",
  minHeight: "48px",
  overflow: "visible",
});

const ProgressBarContainer = styled("div")({
  position: "relative",
  width: "100%",
  height: "24px",
});

const TargetsContainer = styled("div")({
  position: "absolute",
  width: "100%",
  height: "48px",
  top: "50%",
  left: 0,
  transform: "translateY(-50%)",
  pointerEvents: "none",
  zIndex: 10,
});

const TargetGraph = ({
  targets,
  invoiceValue,
  orderbookValue,
  className,
  currency,
  gapToNextTarget,
}: TargetGraphProps) => {
  // Sort targets to ensure they're in ascending order
  const sortedTargets = [...targets].sort((a, b) => a.value - b.value);

  // Calculate the maximum target value for scaling
  const maxTarget = sortedTargets[sortedTargets.length - 1].value || 1;

  return (
    <Root className={className}>
      <ProgressBarContainer>
        <ProgressBar
          currency={currency}
          maxValue={maxTarget}
          invoiceValue={invoiceValue}
          orderbookValue={orderbookValue}
          targets={sortedTargets}
          gapToNextTarget={gapToNextTarget}
        />
        <TargetsContainer>
          {sortedTargets.map((target, index) => {
            // Calculate position as percentage of maxTarget
            const position = (target.value / maxTarget) * 100;

            // Get cashback rate for this target
            const cashbackRate = targets[index].rate;

            // Calculate gap to this specific target
            const gapToTarget = targets[index].gapToTarget;

            return (
              <TargetCircle
                key={index}
                index={index}
                currency={currency}
                targetValue={target.value}
                invoiceValue={invoiceValue}
                position={position}
                cashbackRate={cashbackRate}
                gapToTarget={gapToTarget}
              />
            );
          })}
        </TargetsContainer>
      </ProgressBarContainer>
    </Root>
  );
};

export default memo(TargetGraph);
